/** A `responseType: 'blob'` error body is a Blob; read it as a JSON object. */
export async function extractBlobErrorPayload(data: unknown): Promise<Record<string, unknown> | null> {
  if (!(data instanceof Blob)) return null;
  try {
    const parsed: unknown = JSON.parse(await data.text());
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // not JSON
  }
  return null;
}

/** A `responseType: 'blob'` error body is a Blob; read its JSON `error` field. */
export async function extractBlobErrorMessage(data: unknown): Promise<string | null> {
  const error = (await extractBlobErrorPayload(data))?.error;
  return typeof error === 'string' && error.trim() ? error : null;
}

/** The code the server answers a 해설지 분리 answers request with when nothing in it prints an answer. */
export const NO_ANSWER_CONTENT_CODE = 'no_answer_content';

/** Shown when only the questions file was downloaded because the answers file would be empty. */
export const NO_ANSWER_CONTENT_QUESTIONS_ONLY_MESSAGE =
  '선택한 유형에는 정답지에 들어갈 내용이 없어 문제지만 다운로드했습니다.';

/** The code the server answers a questions-file request with when it would print no row. */
export const NO_QUESTION_CONTENT_CODE = 'no_question_content';

/** Shown when only the answers file was downloaded because the questions file would be empty. */
export const NO_QUESTION_CONTENT_ANSWERS_ONLY_MESSAGE =
  '선택한 문제에는 문제지에 넣을 내용이 없어 정답지만 다운로드했습니다.';

/** Whether a document request was refused with `code` (on the error or its blob body). */
async function isRefusedWithCode(error: unknown, code: string): Promise<boolean> {
  if ((error as { code?: unknown } | null)?.code === code) return true;
  const payload = await extractBlobErrorPayload((error as { details?: unknown } | null)?.details);
  return payload?.code === code;
}

/** Whether a document request was refused with NO_ANSWER_CONTENT_CODE (on the error or its blob body). */
export function isNoAnswerContentError(error: unknown): Promise<boolean> {
  return isRefusedWithCode(error, NO_ANSWER_CONTENT_CODE);
}

/** Whether a document request was refused with NO_QUESTION_CONTENT_CODE (on the error or its blob body). */
export function isNoQuestionContentError(error: unknown): Promise<boolean> {
  return isRefusedWithCode(error, NO_QUESTION_CONTENT_CODE);
}
